import React, { useRef, useState } from "react";
import BootstrapTable from 'react-bootstrap-table-next';
import useInventoryReportLog from "../Hooks/useInventoryReportLog";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Col, FormGroup, Nav, NavItem, Row, Tab } from "react-bootstrap";
import Loader from "../../../components/Common/Loader";
import InventoryProtocolFilter from "../Shared/InventoryProtocolFilter";
import { DateRangeComponent, dateRangeRowStyle } from "../../../components/Common/DateRange";
import InventoryMedicationService from "../../../api/Inventory/InventoryMedicationService";
import AutoCompletation from "../../../components/Common/AutoCompletation";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { Add, Export, Remove } from "../../../components/Icons/Icons";
import { useFormModal } from "../../../components/Modals/formModal.js/FormModal";
import ReleaseList from "../Shared/ReleaseList";
import { AssignmentType } from "../../../api/Inventory/Entities/InventoryEntities";
import { searchPatients } from "../../../api/database";
import InventoryItemsService from "../../../api/Inventory/InventoryItemsService";
import { useAuthorization } from '../../../components/Authorization/useAuthorization';
import { buildMedicationLabel } from '../Shared/inventoryFormRules';

const keys = {
    pharmacyLogs: "pharmacyLogs",
    protocolLogs: "protocolLogs",
    protocolPharmacyLogs: "protocolPharmacyLogs",
}

export default function InventoryReportLog() {
    const [activeTab, setActiveTab] = useState(keys.pharmacyLogs)
    const { t } = useTranslation();
    const [, , isAuthorized] = useAuthorization();

    const handleSelectTab = (key) => setActiveTab(key)

    return (
        <div style={{ padding: "1rem", backgroundColor: "white" }}>
            <Tab.Container id="inventory-managment" activeKey={activeTab} onSelect={handleSelectTab} >
                <Row className="clearfix">
                    <Col sm={12}>
                        <Nav bsStyle="tabs" >
                            {
                                isAuthorized(["GeneralInventory.Create"]) && <NavItem eventKey={keys.pharmacyLogs} >{t('inventory.reports.concomitantMedication')}</NavItem>
                            }
                            {
                                isAuthorized(["ProtocolInventory.Create"]) && <NavItem eventKey={keys.protocolPharmacyLogs} >{t('inventory.reports.protocolConcomitantMedication')}</NavItem>
                            }
                            {
                                isAuthorized(["ProtocolInventory.Create"]) && <NavItem eventKey={keys.protocolLogs} >{t('inventory.reports.IMP')}</NavItem>
                            }
                        </Nav>

                    </Col>
                    <Col sm={12} style={{ paddingTop: "1rem" }}>
                        <Tab.Content>
                            <Tab.Pane eventKey={keys.pharmacyLogs}>
                                {activeTab == keys.pharmacyLogs && <InventoryLogsTable reportType={1} />}
                            </Tab.Pane>
                            <Tab.Pane eventKey={keys.protocolPharmacyLogs}>
                                {activeTab == keys.protocolPharmacyLogs && <InventoryLogsTable reportType={3} useProtocolFilter={true} />}
                            </Tab.Pane>
                            <Tab.Pane eventKey={keys.protocolLogs}>
                                {activeTab == keys.protocolLogs && <InventoryLogsTable reportType={2} useProtocolFilter={true} />}
                            </Tab.Pane>
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </div>
    );
}

function InventoryLogsTable({ reportType, useProtocolFilter = false }) {
    const { t } = useTranslation();
    const [inventoryLog, isLoading, navigation] = useInventoryReportLog({ reportType: reportType });
    const [selectedProtocol, setSelectedProtocol] = useState(null);
    const [dateRange, setDateRange] = useState({
        from: moment().subtract(1, 'month').toDate(),
        to: moment().toDate()
    })
    const medicationRef = useRef();
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const [ReleaseModal, ReleaseActions] = useFormModal({ FormContent: ReleaseList })
    const [selectedPatient, setSelectedPatient] = useState(null)
    const patientRef = useRef(null)

    const isProtocolReport = reportType == 2;
    const isPharmacyReport = reportType == 1;

    const handleSearch = async () => {
        await navigation.search({
            protocolId: selectedProtocol,
            fromDate: dateRange.from,
            toDate: dateRange.to,
            patientNumber: selectedPatient?.medicalRecordNumber,
            inventoryItemId: selectedItem?.inventoryItemId,
            releaseId: selectedRelease?.id
        });
    }

    const columns = [

        { dataField: 'presentation', text: t('commons.description'), headerStyle: { width: '15rem' } },
        { dataField: 'lotNumber', text: t('inventory.commons.lotNumber'), headerStyle: { width: '10rem' } },
        {
            text: 'Expiration Date', headerStyle: { width: '5rem' },
            formatter: function (_cell, item, rowIndex) {
                return item.expirationDate
                    ? moment(item.expirationDate).format("DD/MM/YYYY")
                    : "-";
            }
        },
        { dataField: 'state', text: t('commons.state'), headerStyle: { width: '10rem' }, formatter: (cell, row) => t(`inventory.stock.state.${row.state.toLowerCase()}`) },
        ...(isProtocolReport ? [{
            dataField: 'condition', text: t('inventory.commons.condition'), headerStyle: { width: '8rem' },
            formatter: (cell, row) => row.condition === 'Damaged'
                ? <span className="label label-warning">{t('inventory.stock.condition.damaged')}</span>
                : "-"
        }] : []),
        { dataField: 'patientNumber', text: t('notes.externalNotes.patientNumber'), headerStyle: { width: '10rem' } },
        {
            text: 'Dispensed Date', headerStyle: { width: '5rem' },
            formatter: function (_cell, item, rowIndex) {
                return item.dispensedDate
                    ? moment(item.dispensedDate).format("DD/MM/YYYY")
                    : "-";
            }
        },
        {
            text: 'Return Date', headerStyle: { width: '5rem' },
            formatter: function (_cell, item, rowIndex) {
                return item.returnDate
                    ? moment(item.returnDate).format("DD/MM/YYYY")
                    : "-";
            }
        },
        { dataField: 'kitNumber', text: t('inventory.commons.kitNumber'), headerStyle: { width: '8rem' } },
    ]

    async function fetchItems(value) {
        const result = (await InventoryMedicationService.Find({ term: value, onlyLocal: true, protocolId: selectedProtocol, pageSize: 100 }))
            .Value;
        const hasExactMatch = result.some(item => item.match === 'Exact');
        if (hasExactMatch) {
            const filteredResult = result.filter(item => item.match === 'Exact');
            const item = filteredResult[0];
            medicationRef.current.setInputValue(item);
            setSelectedItem(item);
            return [];
        }

        return result;
    }

    const handleShowReleaseModal = async () => {
        const protocolItemId = isProtocolReport
            ? (await InventoryItemsService.FindProtocolInventory({
                protocolId: selectedProtocol
            })).Values.find(item => item.type == "Protocol")?.id
            : null;

        await ReleaseActions.Show({
            title: t('inventory.commons.lotNumber'),
            onConfirmation: (selected) => setSelectedRelease(selected),
            initValue: {
                itemId: protocolItemId ?? selectedItem.inventoryItemId,
                assignmentType: isPharmacyReport ? AssignmentType.General.Name : AssignmentType.Protocol.Name,
                assignId: selectedProtocol,
                includeExpired: true,
                onlyAvailable: false,
                includeStock: false,
                onlyWithStock: false,
                showStock: false
            }
        })
    }

    const onCleanSelectedRelease = () => {
        setSelectedRelease(null)
    }

    const fetchPatients = async (term) => {
        const result = (await searchPatients(term))
            .data

        return result;
    }

    const curringSelected = (callback) => {
        return (selected) => {
            if (selected != null && selected.length > 0) callback(selected[0])
            else callback(null)
        }
    }

    const downloadExcel = async () => {
        const response = await navigation.downloadExcel({
            protocolId: selectedProtocol,
            fromDate: dateRange.from,
            toDate: dateRange.to,
            patientNumber: selectedPatient?.medicalRecordNumber,
            inventoryItemId: selectedItem?.inventoryItemId,
            releaseId: selectedRelease?.id
        })
    }

    return (<div>
        <div>
            <Row>
                <Col md={6}>
                    {useProtocolFilter && <InventoryProtocolFilter onSelectedProtocol={setSelectedProtocol} />}
                </Col>
            </Row>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "space-between" }}>
                <Row style={dateRangeRowStyle}>
                    <Col md={4}>
                        <DateRangeComponent startDate={dateRange.from} endDate={dateRange.to} handleChange={setDateRange} />
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <AutoCompletation
                                id={'Paciente'}
                                ref={patientRef}
                                labelKey={(option) => {
                                    return `${option.fullName} (${option.medicalRecordNumber})`;
                                }}
                                placeholder={t('commons.patient')}
                                disabled={false}
                                fetchOptions={fetchPatients}
                                onChange={curringSelected(setSelectedPatient)}
                            />
                        </FormGroup>
                    </Col>
                    {!isProtocolReport && <Col sm={2}>
                        <FormGroup>
                            <AutoCompletation
                                id={'medication'}
                                ref={medicationRef}
                                labelKey={(option) => buildMedicationLabel(option)}
                                placeholder={t('inventory.commons.searchMedication')}
                                fetchOptions={fetchItems}
                                onChange={curringSelected(setSelectedItem)}
                                onNext={() => { }}
                            />
                        </FormGroup>
                    </Col>}
                    <Col md={4}>
                        {selectedRelease == null
                            && <div className='input-group-container'>
                                <FormGroup style={{ width: "100%" }}>
                                    <button className="btn btn-primary" disabled={(selectedItem == null && !isProtocolReport) || (isProtocolReport && selectedProtocol == null)} onClick={_ => handleShowReleaseModal()}>
                                        <Tooltip>
                                            <Add /> {t('inventory.dispense.selectLotNumber')}
                                        </Tooltip>
                                    </button>
                                </FormGroup>
                            </div>
                            || <div className='input-group-container'>
                                <FormGroup style={{ width: "100%" }}>
                                    <input disabled={true} value={selectedRelease?.lotNumber} className="rbt-input-main form-control rbt-input" />
                                </FormGroup>

                                <FormGroup style={{ width: "100%" }}>
                                    <button className="btn btn-danger" disabled={selectedRelease == null} onClick={onCleanSelectedRelease}>
                                        <Tooltip tooltip={t('commons.clear')} id={"tooltipedit-limpiarpaciente"}>
                                            <span aria-hidden="true"><Remove /></span>
                                        </Tooltip>
                                    </button>
                                </FormGroup>
                            </div>}
                    </Col>
                </Row>
                <Row style={{ marginTop: "1rem" }}>
                    <Col md={2} style={{ display: "flex", gap: "0.5rem" }}>
                        <button className="btn btn-primary" onClick={handleSearch} disabled={(useProtocolFilter && selectedProtocol == null) || false}>{t("commons.search")}</button>
                        <Tooltip tooltip={t('inventory.reports.downloadExcel')}>
                            <button className="btn btn-primary" onClick={downloadExcel} disabled={(useProtocolFilter && selectedProtocol == null) || false}>
                                <Export />
                            </button>
                        </Tooltip>
                    </Col>
                </Row>
            </div>
        </div>

        <div>
            <Loader isLoading={isLoading}>
                <BootstrapTable
                    bordered={false}
                    hover
                    remote
                    condensed
                    keyField='id'
                    classes="table-spaced table-spaced-small"
                    wrapperClasses="table-responsive"

                    onTableChange={() => { }}

                    data={inventoryLog?.items ?? []}
                    columns={columns}
                    loading={isLoading}
                    noDataIndication={() => <>{t("commons.noData")}</>}
                />
            </Loader>
        </div>
        <ReleaseModal />
    </div>)
}


